import type { DonationLocationValue } from "@project/donate/components/donation-location-fields";
import type { DonorInfo } from "@project/donate/components/donor-types";
import {
  DEFAULT_PHONE_COUNTRY,
  INDIA_MOBILE_REGEX,
  isValidMobileNumber,
  sanitizeMobileNumber,
} from "@project/lib/mobile-number";

export const MOBILE_REGEX = INDIA_MOBILE_REGEX;
export const PAN_REGEX = /^[A-Z]{5}\d{4}[A-Z]$/;
export const AADHAAR_REGEX = /^\d{12}$/;
export const PINCODE_REGEX = /^\d{6}$/;
export const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export type DonorFieldErrors = Partial<
  Record<keyof DonorInfo | "district" | "city" | "area", string>
>;

export function sanitizeMobile(
  value: string,
  countryCode: string = DEFAULT_PHONE_COUNTRY
): string {
  return sanitizeMobileNumber(value, countryCode);
}

export function sanitizeAadhaar(value: string): string {
  return value.replace(/\D/g, "").slice(0, 12);
}

export function sanitizePan(value: string): string {
  return value
    .replace(/[^A-Za-z0-9]/g, "")
    .toUpperCase()
    .slice(0, 10);
}

export function sanitizePincode(value: string): string {
  return value.replace(/\D/g, "").slice(0, 6);
}

export function formatAadhaarDisplay(value: string): string {
  const digits = sanitizeAadhaar(value);
  return digits.replace(/(\d{4})(?=\d)/g, "$1 ").trim();
}

export function validateMobile(
  mobile: string,
  required = true,
  countryCode: string = DEFAULT_PHONE_COUNTRY
): string | undefined {
  const trimmed = mobile.trim();
  if (!trimmed) {
    return required ? "Mobile number is required." : undefined;
  }
  if (!isValidMobileNumber(trimmed, countryCode, { required })) {
    return "Enter a valid mobile number for the selected country.";
  }
  return undefined;
}

export function validatePan(pan: string, required = false): string | undefined {
  const trimmed = pan.trim().toUpperCase();
  if (!trimmed) {
    return required ? "PAN is required for 80G receipt." : undefined;
  }
  if (!PAN_REGEX.test(trimmed)) {
    return "Enter a valid PAN (e.g. ABCDE1234F).";
  }
  return undefined;
}

export function validateAadhaar(aadhaar: string): string | undefined {
  const digits = aadhaar.replace(/\s/g, "");
  if (!digits) {
    return undefined;
  }
  if (!AADHAAR_REGEX.test(digits)) {
    return "Enter a valid 12-digit Aadhaar number.";
  }
  return undefined;
}

export function validateEmail(
  email: string,
  required = false
): string | undefined {
  const trimmed = email.trim();
  if (!trimmed) {
    return required ? "Email is required." : undefined;
  }
  if (!EMAIL_REGEX.test(trimmed)) {
    return "Enter a valid email address.";
  }
  return undefined;
}

export function validatePincode(
  pincode: string,
  required = false
): string | undefined {
  const trimmed = pincode.trim();
  if (!trimmed) {
    return required ? "Pincode is required." : undefined;
  }
  if (!PINCODE_REGEX.test(trimmed)) {
    return "Enter a valid 6-digit pincode.";
  }
  return undefined;
}

type ValidateCheckoutDonorArgs = {
  donor: DonorInfo;
  location: DonationLocationValue;
  isCampaign: boolean;
  want80g: boolean;
};

export function validateCheckoutDonorDetails({
  donor,
  location,
  isCampaign,
  want80g,
}: ValidateCheckoutDonorArgs): {
  errors: DonorFieldErrors;
  message?: string;
} {
  const errors: DonorFieldErrors = {};

  if (!donor.name.trim()) {
    errors.name = "Full name is required.";
  } else if (donor.name.trim().length < 2) {
    errors.name = "Enter a valid full name.";
  }

  const mobileError = validateMobile(donor.mobile, true, donor.mobile_country);
  if (mobileError) {
    errors.mobile = mobileError;
  }

  if (donor.alt_mobile.trim()) {
    const altError = validateMobile(
      donor.alt_mobile,
      false,
      donor.alt_mobile_country
    );
    if (altError) {
      errors.alt_mobile =
        "Enter a valid alternate mobile for the selected country.";
    }
  }

  const emailError = validateEmail(donor.email, isCampaign);
  if (emailError) {
    errors.email = emailError;
  }

  if (isCampaign && !donor.address.trim()) {
    errors.address = "Address is required.";
  }

  const pincodeError = validatePincode(donor.pincode, isCampaign);
  if (pincodeError) {
    errors.pincode = pincodeError;
  }

  if (isCampaign) {
    if (!location.district_id) {
      errors.district = "District is required.";
    }
    if (!location.city_id) {
      errors.city = "City is required.";
    }
    if (!location.area_id) {
      errors.area = "Area is required.";
    }
  }

  const panError = validatePan(donor.pan, want80g);
  if (panError) {
    errors.pan = panError;
  }

  const aadhaarError = validateAadhaar(donor.aadhaar);
  if (aadhaarError) {
    errors.aadhaar = aadhaarError;
  }

  const message = Object.values(errors)[0];
  return { errors, message };
}
